import { api } from './client';
import type { WalletDocumentResponse, Uuid } from '../types/api';
import { compressImageIfNeeded } from '../../lib/compress-image';

export interface WalletDocumentCategory {
  code: string;
  label: string;
  icon?: string;
}

export async function getWalletDocumentCategories(): Promise<WalletDocumentCategory[]> {
  const { data } = await api.get<{ categories: WalletDocumentCategory[] }>(
    '/wallet-documents/categories',
  );
  return data.categories;
}

export async function getWalletDocuments(params?: {
  category?: string;
  search?: string;
}): Promise<WalletDocumentResponse[]> {
  const queryParams = new URLSearchParams();
  if (params?.category && params.category.toUpperCase() !== 'ALL') {
    queryParams.append('category', params.category);
  }
  if (params?.search && params.search.trim()) {
    queryParams.append('search', params.search.trim());
  }
  const queryString = queryParams.toString();
  const url = queryString ? `/wallet-documents?${queryString}` : '/wallet-documents';
  const { data } = await api.get<{ items: WalletDocumentResponse[] }>(url);
  return data.items;
}

export async function getWalletDocument(id: Uuid): Promise<WalletDocumentResponse> {
  const { data } = await api.get<WalletDocumentResponse>(`/wallet-documents/${id}`);
  return data;
}

export async function uploadWalletDocument(
  file: File,
  documentName?: string,
  documentType?: string,
): Promise<WalletDocumentResponse> {
  const processedFile = await compressImageIfNeeded(file);
  const formData = new FormData();
  formData.append('file', processedFile);
  if (documentName) {
    formData.append('documentName', documentName);
  }
  if (documentType) {
    formData.append('documentType', documentType);
  }

  const { data } = await api.post<WalletDocumentResponse>('/wallet-documents', formData, {
    timeout: 120_000,
  });
  return data;
}

export async function updateWalletDocument(
  id: Uuid,
  payload: { documentName?: string; documentType?: string; file?: File },
): Promise<WalletDocumentResponse> {
  const formData = new FormData();
  if (payload.documentName) formData.append('documentName', payload.documentName);
  if (payload.documentType !== undefined) formData.append('documentType', payload.documentType);
  if (payload.file) {
    const processedFile = await compressImageIfNeeded(payload.file);
    formData.append('file', processedFile);
  }

  const { data } = await api.patch<WalletDocumentResponse>(`/wallet-documents/${id}`, formData, {
    timeout: 120_000,
  });
  return data;
}

export async function deleteWalletDocument(id: Uuid): Promise<WalletDocumentResponse> {
  const { data } = await api.delete<WalletDocumentResponse>(`/wallet-documents/${id}`);
  return data;
}

export async function sendWalletDocumentToChat(id: Uuid, transactionId: Uuid): Promise<any> {
  const { data } = await api.post(`/wallet-documents/${id}/send-to-chat/${transactionId}`);
  return data;
}
